import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import './AuthComplete.css';
import { useLanguage } from '../../../context/LanguageContext';


const AuthComplete = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [processingComplete, setProcessingComplete] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const { language } = useLanguage();

    const translations = {
        en: {
            signingIn: "Signing you in…",
            pleaseWait: "Please wait a moment ✨",
        },
        ar: {
            signingIn: "جاري تسجيل دخولك...",
            pleaseWait: "يرجى الانتظار ✨",
        },
    };

    const t = translations[language] || translations.en;

    // When done loading → trigger fadeOut first, then unmount
    useEffect(() => {
        if (!loading) {
            setFadeOut(true);
            const timer = setTimeout(() => setFadeOut(false), 500); // match CSS duration
            return () => clearTimeout(timer);
        }
    }, [loading]);

    const {
        currentUser,
        completeEmailLinkSignIn,
        checkForEmailLink,
        loading: userLoading
    } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    // Get redirect path from query params or state
    const getRedirectPath = () => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get('redirect') || '/';
    };

    // Check if user is already authenticated
    useEffect(() => {
        if (!userLoading && !loading && !processingComplete && currentUser) {
            // console.log('AuthComplete - User already authenticated, redirecting');
            setSuccess(true);
            setTimeout(() => {
                const destination = getRedirectPath();
                navigate(destination, { replace: true });
            }, 1000);
        }
    }, [currentUser, userLoading, loading, processingComplete, navigate]);

    useEffect(() => {
        // Prevent processing if user is already authenticated or we've already processed
        if (currentUser || processingComplete || userLoading) {
            if (currentUser) {
                setLoading(false);
            }
            return;
        }

        // CRITICAL FIX: Only run if current URL is actually an email link
        const currentUrl = window.location.href;
        if (!currentUrl.includes('oobCode') || !currentUrl.includes('mode=signIn')) {
            // console.log('Not on email link URL, skipping email link completion');
            setLoading(false);
            return;
        }

        const handleEmailLinkSignIn = async () => {
            try {
                // console.log('Processing user email link sign-in:', currentUrl);

                // SIMPLIFIED - Just check if it's a valid Firebase email link
                if (!checkForEmailLink()) {
                    // console.log("Not a valid email link - redirecting to login");
                    setError("Invalid authentication link. Redirecting to login...");
                    setProcessingComplete(true);
                    setTimeout(() => navigate('/auth', { replace: true }), 2000);
                    return;
                }

                // console.log('Valid email link found, completing sign-in');

                // Complete the sign-in process
                const result = await completeEmailLinkSignIn();

                if (result.user) {
                    // console.log('User sign-in successful');
                    setSuccess(true);
                    setProcessingComplete(true);

                    // Show success message briefly, then redirect
                    setTimeout(() => {
                        const destination = getRedirectPath();
                        navigate(destination, { replace: true });
                    }, 2000);
                } else {
                    throw new Error('Sign-in completed but no user data received');
                }

            } catch (err) {
                console.error('Error completing user email link sign-in:', err);
                setProcessingComplete(true);

                let errorMessage = err.message || 'Failed to complete sign-in. Please try again.';
                setError(errorMessage);

                // Redirect to auth page after showing error
                setTimeout(() => navigate('/auth', { replace: true }), 3000);
            } finally {
                setLoading(false);
            }
        };

        handleEmailLinkSignIn();
    }, [checkForEmailLink, completeEmailLinkSignIn, currentUser, processingComplete, userLoading, navigate]);

    const handleRetry = () => {
        localStorage.removeItem('emailForSignIn');
        localStorage.removeItem('pendingStoreId');
        localStorage.removeItem('pendingUserData');
        navigate('/auth', { replace: true });
    };


    if (loading) {

        return (
            <div className={`auth-complete-container ${!loading ? "fade-out" : ""}`}>
                <div className="auth-complete-card">
                    <div className="spinner"></div>
                    <h3>{t.signingIn}</h3>
                    <p className="muted-text">{t.pleaseWait}</p>
                </div>
            </div>
        );
    }


    return null; // after redirect, nothing is shown
};

export default AuthComplete;


